import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { tokenStore } from './tokenStore';
import { toApiError } from './errors';

/** Cookie the API sets for double-submit CSRF protection. */
const CSRF_COOKIE = 'astershield_csrf';

/** Header the API expects that cookie echoed back in. */
const CSRF_HEADER = 'X-CSRF-Token';

/** Endpoints that must never trigger a refresh attempt. */
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password'];

/** Axios config plus our own retry marker. */
interface RetriableRequest extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/**
 * Called when the session is definitively over, so the app can redirect.
 * Set by `AuthProvider` rather than imported, to avoid a circular dependency
 * between the HTTP layer and the React tree.
 */
let onSessionExpired: (() => void) | null = null;

/** Registers the callback fired when refresh fails and the user must sign in. */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

/**
 * The in-flight refresh, if one is running.
 *
 * THIS IS THE SINGLE-FLIGHT GUARD, and it is not optional.
 *
 * When an access token expires, every queued request fails with 401 at
 * essentially the same moment — a dashboard firing five parallel queries will
 * produce five 401s. Without this, each one starts its own refresh. The first
 * rotates the token and the rest present a token that has just been superseded,
 * which the server correctly reads as replay of a stolen token and responds to by
 * revoking the entire family — logging the user out.
 *
 * Holding one shared promise means exactly one refresh happens and the others
 * await its result. It is the client half of a pair; the server half is the
 * 30-second grace window that forgives a genuine race between browser tabs, which
 * this cannot prevent because separate tabs do not share this module.
 */
let refreshPromise: Promise<string> | null = null;

/** Reads a cookie value by name. */
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** True when the URL is one of the auth endpoints. */
function isAuthPath(url: string | undefined): boolean {
  return !!url && AUTH_PATHS.some((path) => url.includes(path));
}

export const http: AxiosInstance = axios.create({
  baseURL: '/api',

  // Required for the HttpOnly refresh cookie and the CSRF cookie to travel.
  withCredentials: true,

  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request: attach credentials ──────────────────────────────────────────────
http.interceptors.request.use((config) => {
  const token = tokenStore.get();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // The CSRF header is only needed on the cookie-authenticated endpoints. Every
  // other request authenticates with a Bearer header, which a cross-site form
  // cannot set — making them structurally immune to CSRF.
  const needsCsrf =
    config.url?.includes('/auth/refresh') || config.url?.includes('/auth/logout');

  if (needsCsrf) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      config.headers[CSRF_HEADER] = csrf;
    }
  }

  return config;
});

// ── Response: refresh once, then replay ──────────────────────────────────────
http.interceptors.response.use(
  (response) => response,

  async (error: AxiosError) => {
    const request = error.config as RetriableRequest | undefined;

    const shouldTryRefresh =
      error.response?.status === 401 &&
      request &&
      !request._retried &&
      !isAuthPath(request.url);

    if (!shouldTryRefresh) {
      return Promise.reject(toApiError(error));
    }

    // Marked before awaiting, so a request that fails again after replay falls
    // straight through instead of looping.
    request._retried = true;

    try {
      const token = await refreshAccessToken();
      request.headers.Authorization = `Bearer ${token}`;
      return await http.request(request);
    } catch {
      // Refresh failed: the refresh token is expired, revoked, or was replayed
      // and the family was destroyed. Either way the session is over.
      tokenStore.clear();
      onSessionExpired?.();
      return Promise.reject(toApiError(error));
    }
  },
);

/**
 * Exchanges the refresh cookie for a new access token, at most once at a time.
 *
 * @returns The new access token.
 */
async function refreshAccessToken(): Promise<string> {
  // Concurrent callers await the SAME promise rather than starting their own.
  refreshPromise ??= (async () => {
    try {
      // A bare axios call, not `http`, so this cannot recurse through the
      // response interceptor if the refresh itself returns 401.
      const response = await axios.post<{ accessToken: string }>(
        '/api/auth/refresh',
        {},
        {
          withCredentials: true,
          headers: { [CSRF_HEADER]: readCookie(CSRF_COOKIE) ?? '' },
        },
      );

      const token = response.data.accessToken;
      tokenStore.set(token);
      return token;
    } finally {
      // Cleared in `finally` so a failed refresh does not leave a rejected
      // promise cached — every later request would then fail instantly without
      // ever retrying.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** Performs the boot-time refresh that restores a session after a page reload. */
export async function restoreSession(): Promise<string | null> {
  try {
    return await refreshAccessToken();
  } catch {
    // No valid refresh cookie. Expected on a first visit or after sign-out —
    // not an error worth surfacing.
    return null;
  }
}
